import { useState } from 'react'
import { Sparkles, Loader2, Check, X, Settings, Shield, Fingerprint, Building2 } from 'lucide-react'
import { scopeVersionsApi, type DealTranscript, type HubspotExcerpt } from '@api/client'
import { TranscriptSourcePanel } from './TranscriptSourcePanel'

type ScopeSuggestions = {
  general?: {
    clientUseCase?: string | null
    phases?: string[] | null
    integrationType?: string | null
    platformVersion?: string | null
    forecastChecks12m?: number | null
    ssoAccess?: string | null
    aiFeatures?: string | null
  } | null
  kyc?: {
    kycRequired?: string | null
    complianceFramework?: string | null
    internationalCountries?: string[] | null
  } | null
  aml?: {
    amlRequired?: string | null
    level?: string | null
    monitoringType?: string | null
  } | null
  biometrics?: {
    documentVerification?: string | null
    selfieVerification?: string | null
  } | null
  kyb?: {
    enabled?: string | null
  } | null
}

type SuggestionResult = {
  suggestions: ScopeSuggestions
  confidence: string
  reasoning: string
  warnings: string[]
}

// Friendly labels for enum values
const ENUM_LABELS: Record<string, Record<string, string>> = {
  integrationType: { api: 'API', portal: 'Portal Only', both: 'Both', unknown: "Don't Know" },
  platformVersion: { v1: 'V1', v2: 'V2', unknown: "Don't Know" },
  ssoAccess: { yes: 'Yes', no: 'No', unknown: "Don't Know" },
  aiFeatures: { yes: 'Yes', no: 'No', unknown: "Don't Know" },
  kycRequired: { yes: 'Yes', no: 'No', unknown: "Don't Know" },
  complianceFramework: { safe_harbour_2x2: 'Safe Harbour 2+2', standard_1x1: 'Standard 1+1', custom: 'Custom', none: 'None', unknown: "Don't Know" },
  amlRequired: { yes: 'Yes', no: 'No', unknown: "Don't Know" },
  level: { standard: 'Standard (Sanctions only)', enhanced: 'Enhanced (Sanctions + PEP + Adverse Media)' },
  monitoringType: { ongoing: 'Ongoing Monitoring', one_off: 'One-off', unknown: "Don't Know" },
  documentVerification: { yes: 'Yes', no: 'No', unknown: "Don't Know" },
  selfieVerification: { yes: 'Yes', no: 'No', unknown: "Don't Know" },
  enabled: { yes: 'Yes', no: 'No', unknown: "Don't Know" },
}

function friendlyLabel(field: string, value: string): string {
  return ENUM_LABELS[field]?.[value] || value
}

interface ScopeSuggestModalProps {
  dealId: number | null
  isOpen: boolean
  onClose: () => void
  onApply: (section: string, field: string, value: unknown) => void
}

export function ScopeSuggestModal({ dealId, isOpen, onClose, onApply }: ScopeSuggestModalProps) {
  const [notes, setNotes] = useState('')
  const [includeHubspot, setIncludeHubspot] = useState(true)
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<SuggestionResult | null>(null)
  const [applied, setApplied] = useState<Set<string>>(new Set())
  const [selectedTranscriptIds, setSelectedTranscriptIds] = useState<Set<number>>(new Set())
  const [savedTranscripts, setSavedTranscripts] = useState<DealTranscript[]>([])
  const [hubspotExcerpt, setHubspotExcerpt] = useState<HubspotExcerpt | null>(null)
  const [excerptLoading, setExcerptLoading] = useState(false)
  const [includeExcerpt, setIncludeExcerpt] = useState(true)

  const toggleTranscript = (id: number) => {
    setSelectedTranscriptIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const handleTranscriptsLoaded = (transcripts: DealTranscript[]) => {
    setSavedTranscripts(transcripts)
    // Auto-select newly loaded transcripts
    setSelectedTranscriptIds(new Set(transcripts.map((t) => t.id)))
  }

  const handleGenerate = async () => {
    if (!dealId) return
    setLoading(true)
    setResult(null)
    setApplied(new Set())

    const transcriptTexts = savedTranscripts
      .filter((t) => selectedTranscriptIds.has(t.id))
      .map((t) => `## Transcript: ${t.title} (${t.call_date})\n${t.transcript}`)
    const combinedNotes = [notes.trim(), ...transcriptTexts].filter(Boolean).join('\n\n---\n\n')

    try {
      const res = await scopeVersionsApi.scopeSuggest(dealId, combinedNotes, includeHubspot || (includeExcerpt && !!hubspotExcerpt))
      if (res.success && res.data) {
        setResult(res.data)
      } else {
        setResult({ suggestions: {}, confidence: 'low', reasoning: res.error || 'Failed to generate suggestions', warnings: [] })
      }
    } catch {
      setResult({ suggestions: {}, confidence: 'low', reasoning: 'Network error — could not reach server', warnings: [] })
    }
    setLoading(false)
  }

  const applyField = (section: string, field: string, value: unknown) => {
    const key = `${section}.${field}`
    onApply(section, field, value)
    setApplied((prev) => new Set([...prev, key]))
  }

  const hasInput = notes.trim() || selectedTranscriptIds.size > 0 || includeHubspot || (includeExcerpt && hubspotExcerpt)

  if (!isOpen) return null

  // Flatten suggestions into renderable cards
  const sections: { id: string; label: string; icon: React.ReactNode; fields: { key: string; label: string; value: unknown; display: string }[] }[] = []

  if (result?.suggestions?.general) {
    const g = result.suggestions.general
    const fields: typeof sections[0]['fields'] = []
    if (g.clientUseCase) fields.push({ key: 'clientUseCase', label: 'Client Use Case', value: g.clientUseCase, display: g.clientUseCase })
    if (g.phases && g.phases.length > 0) fields.push({ key: 'phases', label: 'Phases', value: g.phases, display: g.phases.map((p, i) => `Phase ${i + 1}: ${p}`).join('\n') })
    if (g.integrationType && g.integrationType !== 'unknown') fields.push({ key: 'integrationType', label: 'Integration Type', value: g.integrationType, display: friendlyLabel('integrationType', g.integrationType) })
    if (g.platformVersion && g.platformVersion !== 'unknown') fields.push({ key: 'platformVersion', label: 'Platform Version', value: g.platformVersion, display: friendlyLabel('platformVersion', g.platformVersion) })
    if (g.forecastChecks12m) fields.push({ key: 'forecastChecks12m', label: 'Forecast Checks (12m)', value: g.forecastChecks12m, display: g.forecastChecks12m.toLocaleString() })
    if (g.ssoAccess && g.ssoAccess !== 'unknown') fields.push({ key: 'ssoAccess', label: 'SSO Access', value: g.ssoAccess, display: friendlyLabel('ssoAccess', g.ssoAccess) })
    if (g.aiFeatures && g.aiFeatures !== 'unknown') fields.push({ key: 'aiFeatures', label: 'AI Features', value: g.aiFeatures, display: friendlyLabel('aiFeatures', g.aiFeatures) })
    if (fields.length > 0) sections.push({ id: 'general', label: 'General', icon: <Settings className="w-3.5 h-3.5" />, fields })
  }

  if (result?.suggestions?.kyc) {
    const k = result.suggestions.kyc
    const fields: typeof sections[0]['fields'] = []
    if (k.kycRequired && k.kycRequired !== 'unknown') fields.push({ key: 'kycRequired', label: 'KYC Required', value: k.kycRequired, display: friendlyLabel('kycRequired', k.kycRequired) })
    if (k.complianceFramework && k.complianceFramework !== 'unknown') fields.push({ key: 'complianceFramework', label: 'Compliance Framework', value: k.complianceFramework, display: friendlyLabel('complianceFramework', k.complianceFramework) })
    if (k.internationalCountries && k.internationalCountries.length > 0) fields.push({ key: 'internationalCountries', label: 'International Countries', value: k.internationalCountries, display: k.internationalCountries.join(', ') })
    if (fields.length > 0) sections.push({ id: 'kyc', label: 'KYC', icon: <Shield className="w-3.5 h-3.5" />, fields })
  }

  if (result?.suggestions?.aml) {
    const a = result.suggestions.aml
    const fields: typeof sections[0]['fields'] = []
    if (a.amlRequired) fields.push({ key: 'amlRequired', label: 'AML Required', value: a.amlRequired, display: friendlyLabel('amlRequired', a.amlRequired) })
    if (a.level) fields.push({ key: 'level', label: 'AML Level', value: a.level, display: friendlyLabel('level', a.level) })
    if (a.monitoringType && a.monitoringType !== 'unknown') fields.push({ key: 'monitoringType', label: 'Monitoring Type', value: a.monitoringType, display: friendlyLabel('monitoringType', a.monitoringType) })
    if (fields.length > 0) sections.push({ id: 'aml', label: 'AML', icon: <Shield className="w-3.5 h-3.5" />, fields })
  }

  if (result?.suggestions?.biometrics) {
    const b = result.suggestions.biometrics
    const fields: typeof sections[0]['fields'] = []
    if (b.documentVerification && b.documentVerification !== 'unknown') fields.push({ key: 'documentVerification', label: 'Document Verification', value: b.documentVerification, display: friendlyLabel('documentVerification', b.documentVerification) })
    if (b.selfieVerification && b.selfieVerification !== 'unknown') fields.push({ key: 'selfieVerification', label: 'Selfie / Liveness', value: b.selfieVerification, display: friendlyLabel('selfieVerification', b.selfieVerification) })
    if (fields.length > 0) sections.push({ id: 'biometrics', label: 'Biometrics', icon: <Fingerprint className="w-3.5 h-3.5" />, fields })
  }

  if (result?.suggestions?.kyb) {
    const kb = result.suggestions.kyb
    const fields: typeof sections[0]['fields'] = []
    if (kb.enabled && kb.enabled !== 'unknown') fields.push({ key: 'enabled', label: 'KYB Required', value: kb.enabled, display: friendlyLabel('enabled', kb.enabled) })
    if (fields.length > 0) sections.push({ id: 'kyb', label: 'KYB', icon: <Building2 className="w-3.5 h-3.5" />, fields })
  }

  return (
    <>
      <div className="fixed inset-0 bg-black/60 z-40" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="w-full max-w-3xl bg-[var(--bg-secondary)] rounded-lg shadow-modal" role="dialog" aria-modal="true">
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-3 border-b border-violet-500/20 bg-violet-500/5 rounded-t-lg">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-violet-400" />
              <span className="text-sm font-semibold text-violet-400">AI-Assisted Scope</span>
              <span className="px-1.5 py-0.5 text-[9px] font-bold bg-violet-500/20 text-violet-300 rounded">SUGGESTIONS ONLY</span>
            </div>
            <button onClick={onClose} className="p-1 text-[var(--text-muted)] hover:text-[var(--text-primary)]">
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Body */}
          <div className="p-5 space-y-4 max-h-[calc(100vh-10rem)] overflow-y-auto">
            <p className="text-xs text-[var(--text-secondary)]">
              Select saved transcripts and/or paste additional context. The AI will analyse this alongside HubSpot data to <strong>suggest</strong> values for scope fields across General, KYC, AML, Biometrics, and KYB. You review and apply what makes sense.
            </p>

            <TranscriptSourcePanel
              dealId={dealId}
              isActive={isOpen}
              selectedTranscriptIds={selectedTranscriptIds}
              onToggleTranscript={toggleTranscript}
              onTranscriptsLoaded={handleTranscriptsLoaded}
              includeExcerpt={includeExcerpt}
              onToggleExcerpt={setIncludeExcerpt}
              hubspotExcerpt={hubspotExcerpt}
              onExcerptChange={setHubspotExcerpt}
              excerptLoading={excerptLoading}
              onExcerptLoadingChange={setExcerptLoading}
            />

            {/* Additional notes */}
            <div className="space-y-1.5">
              <p className="text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wide">Additional Notes (optional)</p>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Paste extra context — email threads, Slack messages, or anything not in a saved transcript..."
                className="w-full px-3 py-2 text-xs rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] resize-y"
                rows={3}
              />
            </div>

            <div className="flex items-center justify-between">
              {!hubspotExcerpt && (
                <label className="flex items-center gap-2 text-xs text-[var(--text-secondary)] cursor-pointer">
                  <input type="checkbox" checked={includeHubspot} onChange={(e) => setIncludeHubspot(e.target.checked)} className="rounded border-[var(--border-color)]" />
                  Pull raw context from HubSpot (notes, deal properties)
                </label>
              )}
              {hubspotExcerpt && <div />}
              <button
                onClick={handleGenerate}
                disabled={loading || !dealId || !hasInput}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-white bg-violet-600 hover:bg-violet-500 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Analysing...</> : <><Sparkles className="w-3.5 h-3.5" /> Generate suggestions</>}
              </button>
            </div>

            {!dealId && (
              <p className="text-[11px] text-amber-400">Save this deal first to enable AI suggestions.</p>
            )}

            {/* Results */}
            {result && (
              <div className="space-y-4 pt-3 border-t border-violet-500/20">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`px-2 py-0.5 text-[10px] font-bold rounded ${
                    result.confidence === 'high' ? 'bg-emerald-500/20 text-emerald-400'
                    : result.confidence === 'medium' ? 'bg-amber-500/20 text-amber-400'
                    : 'bg-red-500/20 text-red-400'
                  }`}>
                    {result.confidence} confidence
                  </span>
                  <span className="px-1.5 py-0.5 text-[9px] font-medium bg-[var(--bg-tertiary)] text-[var(--text-muted)] rounded">via Cassidy AI</span>
                  <span className="text-[11px] text-[var(--text-muted)]">{result.reasoning}</span>
                </div>

                {result.warnings.length > 0 && (
                  <div className="text-[11px] text-amber-400">
                    {result.warnings.map((w, i) => <p key={i}>⚠ {w}</p>)}
                  </div>
                )}

                {sections.map((section) => (
                  <div key={section.id} className="space-y-2">
                    <p className="text-[10px] font-semibold text-violet-400 uppercase tracking-wide flex items-center gap-1.5">
                      {section.icon} {section.label}
                    </p>
                    {section.fields.map((f) => {
                      const fieldKey = `${section.id}.${f.key}`
                      const isApplied = applied.has(fieldKey)
                      const isLong = typeof f.display === 'string' && f.display.length > 100
                      return (
                        <div key={fieldKey} className={`rounded-lg border p-3 ${isApplied ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-[var(--border-color)] bg-[var(--bg-primary)]'}`}>
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0 flex-1">
                              <span className="text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wide">{f.label}</span>
                              <p className={`text-xs text-[var(--text-primary)] mt-1 ${isLong ? 'whitespace-pre-wrap' : ''}`}>{f.display}</p>
                            </div>
                            <div className="shrink-0">
                              {isApplied ? (
                                <span className="inline-flex items-center gap-1 text-[10px] text-emerald-400 font-medium"><Check className="w-3 h-3" /> Applied</span>
                              ) : (
                                <button
                                  onClick={() => applyField(section.id, f.key, f.value)}
                                  className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium text-violet-400 bg-violet-500/10 border border-violet-500/25 rounded hover:bg-violet-500/20 transition-colors whitespace-nowrap"
                                >
                                  Use this
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                ))}

                {sections.length === 0 && (
                  <p className="text-xs text-[var(--text-muted)] italic py-2">No suggestions could be generated from the provided context. Try adding more detailed call notes.</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}
